import { apiCall, isBackendConfigured } from './api-call'
import { ConfirmStepUpResult, StartStepUpResult } from './types'

/**
 * Asks the backend to send a security code before the renter reaches a
 * protected action.
 *
 * The destination is not passed in. The server reads it from the renter's
 * stored account, so this call cannot be used to redirect a code somewhere the
 * account owner would never see it.
 *
 * Returns the named outcome rather than throwing on a refusal, because a
 * cooldown or a missing phone number is something the renter needs told, not an
 * error to swallow.
 *
 * @param {string} quickPassUserId
 * @returns {Promise<StartStepUpResult | null>} the outcome, or null when the call itself failed
 */
const startStepUpChallenge = async (
  quickPassUserId: string,
): Promise<StartStepUpResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'startstepupchallenge',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as StartStepUpResult
  } catch (error) {
    console.warn('[step-up] startStepUpChallenge failed', error)
    return null
  }
}

/**
 * Hands the typed code back for checking. A successful check unlocks the
 * protected action for a short window held on the server.
 *
 * @param {string} quickPassUserId
 * @param {string} code
 * @returns {Promise<ConfirmStepUpResult | null>} the outcome, or null when the call itself failed
 */
const confirmStepUpChallenge = async (
  quickPassUserId: string,
  code: string,
): Promise<ConfirmStepUpResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'confirmstepupchallenge',
      data: { quickPassUserId, code },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as ConfirmStepUpResult
  } catch (error) {
    console.warn('[step-up] confirmStepUpChallenge failed', error)
    return null
  }
}

export { startStepUpChallenge, confirmStepUpChallenge }
